import type { ContentPage } from "@/lib/content/types";

// First CRM content page — Salesforce Automation hub. Uses the same typed leaf
// shape as HRMS (template "hrmsFeature"); the CRM catch-all renders it with
// CrmFeaturePage (HRMS design, CRM wording). The four cards link to the
// Salesforce Automation sub-features (built later).
export const salesforceAutomation: ContentPage = {
  slug: "salesforce-automation",
  template: "hrmsFeature",
  seo: {
    title: "Salesforce Automation Software - Automate Your Sales Process",
    description:
      "Automate leads, deals, contacts and workflows with GetBiz CRM Salesforce Automation. Sell faster and smarter with a fully automated sales pipeline.",
    keywords: [
      "salesforce automation",
      "sales force automation software",
      "CRM automation",
      "lead management",
      "deal management",
      "sales workflow automation",
      "GetBiz CRM",
    ],
    ogImage: "/assets/data/seoimages/salesforce-automation.webp",
  },
  leaf: {
    hub: true,
    benefits: [
      { t: "Capture every lead", d: "Auto-capture and route leads from every channel." },
      { t: "Never lose a deal", d: "Track deals across clear, custom pipeline stages." },
      { t: "One source of truth", d: "Contacts and accounts in one connected view." },
      { t: "Automate busywork", d: "No-code rules trigger tasks and follow-ups." },
      { t: "Faster follow-up", d: "Cadences keep prospects engaged automatically." },
      { t: "Full visibility", d: "Real-time status on every lead, deal and account." },
      { t: "Less manual entry", d: "Records update themselves as work happens." },
      { t: "Close faster", d: "Reps spend time selling, not on admin." },
    ],
    process: {
      title: "How Salesforce Automation Works, Step by Step",
      description: "From first touch to closed deal, GetBiz CRM automates each stage of the sales process.",
      steps: [
        { title: "Capture Lead", caption: "Leads flow in from forms, email and every channel.", image: "/assets/images/img/product/crm/capture-lead.webp" },
        { title: "Score & Assign", caption: "Rules score leads and route them to the right rep.", image: "/assets/images/img/product/crm/score-assign.webp" },
        { title: "Work the Deal", caption: "Move deals through custom pipeline stages.", image: "/assets/images/img/product/crm/work-the-deal.webp" },
        { title: "Automate Follow-ups", caption: "Workflows and cadences nudge every stage.", image: "/assets/images/img/product/crm/automate-follow-ups.webp" },
        { title: "Close & Sync", caption: "Won deals update contacts, accounts and reports.", image: "/assets/images/img/product/crm/close-sync.webp" },
      ],
    },
    highlights: {
      title: "Key Highlights of Salesforce Automation",
      items: [
        { image: "/assets/images/img/product/crm/capture-every-lead.webp", title: "Capture and route every lead.", href: "/crm-software/salesforce-automation/lead-management/", description: "Automatically capture, score and route leads from every channel so no opportunity slips through the cracks.", checklist: ["Auto-capture", "Lead scoring", "Smart routing", "No leaks"] },
        { image: "/assets/images/img/product/crm/move-deals.webp", title: "Move deals to close.", href: "/crm-software/salesforce-automation/deal-management/", description: "Track every deal across custom pipeline stages with real-time value, status and win insights.", checklist: ["Custom pipelines", "Real-time value", "Stage tracking", "Win insights"] },
        { image: "/assets/images/img/product/crm/workflow-image.webp", title: "Automate the busywork.", href: "/crm-software/salesforce-automation/workflow-management/", description: "Trigger follow-ups, field updates and tasks with no-code automation rules so your team can focus on selling.", checklist: ["No-code rules", "Triggers", "Auto actions", "Save time"] },
      ],
    },
    hero: {
      caption: "Salesforce Automation",
      title: "Automate Your Sales Process for Faster, Smarter Selling",
      description:
        "Capture leads, move deals through the pipeline, and keep every contact and account in sync — automatically. GetBiz CRM handles the busywork so your team can focus on closing.",
      h1: "caption",
    },
    split: {
      title: "Everything Your Sales Team Needs to Sell Faster",
      bullets: [
        {
          title: "Lead Capture",
          description:
            "Automatically capture and route leads from every channel to the right rep.",
        },
        {
          title: "Deal Pipelines",
          description:
            "Move deals through customizable stages with clear, real-time visibility.",
        },
        {
          title: "360° Contacts",
          description:
            "Every contact and account with full history in one connected view.",
        },
        {
          title: "Workflow Automation",
          description:
            "Trigger follow-ups, updates and tasks with no-code automation rules.",
        },
      ],
      image: "/assets/images/img/product/crm/salesforce-automation.webp",
      imageAlt: "GetBiz CRM salesforce automation dashboard",
    },
    cardsHeading: {
      title: "Key Features of Salesforce Automation",
      description:
        "Everything you need to automate selling — from first touch to closed deal — in one connected CRM.",
    },
    cards: [
      {
        title: "Lead Management",
        href: "/crm-software/salesforce-automation/lead-management/",
        description:
          "Capture, score, and route leads automatically so no opportunity slips through the cracks.",
        image: "/assets/images/img/product/crm/lead-managemnet-crm.webp",
        imageAlt: "CRM lead management",
        checklist: ["Auto-Capture","Lead Scoring","Smart Routing","No Leaks"],
      },
      {
        title: "Deal Management",
        href: "/crm-software/salesforce-automation/deal-management/",
        description:
          "Track every deal across custom pipeline stages with real-time value and status.",
        image: "/assets/images/img/product/crm/deal-management-image-Photoroom.webp",
        imageAlt: "CRM deal management",
        checklist: ["Custom Pipelines","Real-Time Value","Stage Tracking","Win Insights"],
      },
      {
        title: "Contact Management",
        href: "/crm-software/salesforce-automation/contact-management/",
        description:
          "Keep a complete, up-to-date record of every contact and their full interaction history.",
        image: "/assets/images/img/product/crm/contact-management-image.webp",
        imageAlt: "CRM contact management",
        checklist: ["Full History","360° View","Always Updated","One Record"],
      },
      {
        title: "Account Management",
        href: "/crm-software/salesforce-automation/account-management/",
        description:
          "Manage companies, hierarchies, and related contacts and deals in one connected view.",
        image: "/assets/images/img/product/crm/account-management.webp",
        imageAlt: "CRM account management",
        checklist: ["Company View","Hierarchies","Linked Deals","Related Contacts"],
      },
      {
        title: "Workflow Management",
        href: "/crm-software/salesforce-automation/workflow-management/",
        description:
          "Automate repetitive sales tasks with no-code rules, triggers, and actions.",
        image: "/assets/images/img/product/crm/workflow-image.webp",
        imageAlt: "CRM workflow management",
        checklist: ["No-Code Rules","Triggers","Auto Actions","Save Time"],
      },
      {
        title: "Cadences",
        href: "/crm-software/salesforce-automation/cadences/",
        description:
          "Build multi-step outreach sequences that keep prospects engaged automatically.",
        image: "/assets/images/img/product/crm/cadence-image-Photoroom.webp",
        imageAlt: "CRM sales cadences",
        checklist: ["Multi-Step","Auto Outreach","Stay Engaged","Higher Reply"],
      },
    ],
    testimonials: {
      title: "Businesses across India love & trust GetBiz!",
      description: "See why teams choose GetBiz CRM to run their sales.",
    },
    bookDemoTitle: "Ready to automate your sales process?",
    relatedBlogs: "slug",
    relatedBlogsKeyword: "sales-force-automation-guide, sales-automation-benefits, what-is-crm-software",
    metaKey: "salesforce-automation",
  },
  sections: [],
};
